// Client for the AgentsStack streaming Lambda. The base URL points at the
// CloudFront distribution that fronts the lambda Function URL (e.g.
// https://agents.centivo.dev) — or the local server in dev mode.
//
// Auth: we share the same access_token cookie the backend issues. Because
// agents.<domain> and business.<domain> share the registrable parent domain,
// the browser sends it automatically as long as the request goes out with
// `credentials: "include"`. On 401 we run the existing refresh flow against
// the backend's /auth/refresh-token route, then retry once.
import { refreshToken } from "./fetchUtils.js";

// Strip any trailing slash(es) so callers can safely append `/chat`,
// `/threads`, etc. A trailing slash in the deploy-pipeline env value
// (e.g. "https://agents.centivo.live/") otherwise produces a double-slash
// path like "https://agents.centivo.live//chat", which breaks the
// CloudFront -> Lambda Function URL streaming path and surfaces in the
// browser as `net::ERR_HTTP2_PROTOCOL_ERROR` (with a misleading 200 status).
const AGENTS_URL = (import.meta.env.VITE_AGENTS_URL || 'https://agents.centivo.dev').replace(/\/+$/, ''); //!!important add these values in centivo-devops pipeline/src/centivo-business/env when development is done

// Bail loudly if the build was produced without VITE_AGENTS_URL — otherwise
// every URL becomes `${undefined}/agents/...` which the browser silently
// resolves against the current origin (e.g. business.centivo.dev/undefined/agents)
// and we end up parsing the SPA's index.html as JSON. This used to cost an
// hour of debugging; better to fail at boot. The check runs once at module
// load — a missing env file in the deploy pipeline will surface immediately
// in the console, before any user action.
if (!AGENTS_URL) {
    // eslint-disable-next-line no-console
    console.error(
        "[agentsApi] VITE_AGENTS_URL is not set. The frontend was built "
        + "without a value for this environment — check that the env file "
        + "for the active vite mode (e.g. .env.development / .env.staging "
        + "/ .env.prod) defines VITE_AGENTS_URL.",
    );
}
const requireAgentsUrl = () => {
    if (!AGENTS_URL) {
        throw new Error(
            "VITE_AGENTS_URL is not configured for this build. Add it to the "
            + "Vite env file used by the deploy pipeline and rebuild.",
        );
    }
    return AGENTS_URL;
};

// Local dev fallback. The cookie auth path requires the request to be
// SameSite with the cookie's `.centivo.<tld>` parent domain. Two scenarios
// where that breaks and we have to fall back to an `x-api-key` header:
//
//   1. AGENTS_URL itself is localhost (developer running `bun run dev` for
//      both the agents server and the frontend) — there's no centivo.<tld>
//      cookie to share at all.
//   2. The PAGE is loaded from localhost but AGENTS_URL points at deployed
//      agents.centivo.<tld>. The browser refuses to send a SameSite=Lax
//      cookie scoped to `.centivo.<tld>` on a cross-site fetch initiated
//      by a localhost page.
//
// In either case, fall back to the api-key. The deployed agents lambda's
// auth resolver (packages/agents/src/auth.js) accepts a non-empty
// `x-api-key` and resolves it to a userId for dev callers.
const isLocalAgents = /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(AGENTS_URL || "");
const isLocalPage = typeof window !== "undefined"
    && /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(window.location.origin || "");
const useDevApiKey = isLocalAgents || isLocalPage;
const getDevApiKey = () => {
    if (!useDevApiKey) return "";
    return localStorage.getItem("agents_api_key") || "shhh";
};

const buildHeaders = () => {
    const h = { "Content-Type": "application/json" };
    const devKey = getDevApiKey();
    if (devKey) h["x-api-key"] = devKey;
    return h;
};

const baseFetchInit = () => ({
    headers: buildHeaders(),
    credentials: "include",
});

// Wraps fetch with a single 401-retry that pre-flights the backend refresh
// route. Mirrors fetchWithRetry's behaviour but keeps the agents client
// independent of the backend's BACKEND_URL plumbing.
const agentsFetch = async (url, init = {}, { retried = false } = {}) => {
    const merged = {
        ...init,
        credentials: "include",
        headers: { ...buildHeaders(), ...(init.headers || {}) },
    };
    const res = await fetch(url, merged);
    if (res.status !== 401 || retried) return res;
    try {
        await refreshToken();
    } catch {
        return res;
    }
    return fetch(url, merged);
};

const handle = async (res) => {
    if (!res.ok) {
        let body;
        try {
            body = await res.json();
        } catch {
            body = { message: await res.text() };
        }
        throw new Error(body.message || `Request failed: ${res.status}`);
    }
    return res.json();
};

export const listAgents = async () =>
    handle(await agentsFetch(`${requireAgentsUrl()}/agents`));

export const listThreads = async ({ agentId, limit, cursor } = {}) => {
    const url = new URL(`${requireAgentsUrl()}/threads`);
    if (agentId) url.searchParams.set("agentId", agentId);
    if (limit) url.searchParams.set("limit", String(limit));
    if (cursor) url.searchParams.set("cursor", cursor);
    return handle(await agentsFetch(url.toString()));
};

export const getThread = async ({ threadId, limit, before } = {}) => {
    const url = new URL(`${requireAgentsUrl()}/threads/${threadId}`);
    if (limit) url.searchParams.set("limit", String(limit));
    if (before != null) url.searchParams.set("before", String(before));
    return handle(await agentsFetch(url.toString()));
};

export const getThreadUpdates = async ({ threadId, limit, before } = {}) => {
    const url = new URL(`${requireAgentsUrl()}/threads/${threadId}/updates`);
    if (limit) url.searchParams.set("limit", String(limit));
    if (before != null) url.searchParams.set("before", String(before));
    return handle(await agentsFetch(url.toString()));
};

export const deleteThread = async ({ threadId }) =>
    handle(await agentsFetch(`${requireAgentsUrl()}/threads/${threadId}`, {
        method: "DELETE",
    }));

// Operator smiley (1–5) after a successful claim / authorization create.
// Fire-and-forget from the UI — the server persists then learns in-handler
// after the 200 is already on the wire.
export const submitRating = async ({ threadId, targetId, score, toolName, agentId }) =>
    handle(await agentsFetch(`${requireAgentsUrl()}/threads/${threadId}/rating`, {
        method: "POST",
        body: JSON.stringify({ targetId, score, toolName, agentId }),
    }));

// Two-step upload:
//   1. Ask the backend for a signed PUT URL for {name, mime, size}
//   2. PUT the file bytes directly to S3/minio at uploadUrl
// Returns the canonical attachment metadata you should send back to /chat.
export const presignUpload = async ({ name, mime, size }) =>
    handle(await agentsFetch(`${requireAgentsUrl()}/uploads/presign`, {
        method: "POST",
        body: JSON.stringify({ name, mime, size }),
    }));

export const uploadAttachment = async (file, { onProgress } = {}) => {
    const presigned = await presignUpload({
        name: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
    });

    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presigned.uploadUrl, true);
        for (const [k, v] of Object.entries(presigned.headers || {})) {
            xhr.setRequestHeader(k, v);
        }
        xhr.upload.onprogress = (ev) => {
            if (onProgress && ev.lengthComputable) {
                onProgress(ev.loaded / ev.total);
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror = () => reject(new Error("Upload failed (network error)"));
        xhr.send(file);
    });

    return {
        key: presigned.key,
        name: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        downloadUrl: presigned.downloadUrl,
    };
};

// Walks an SSE response body and yields each parsed `data: ...` payload.
async function* parseSseStream(res) {
    if (!res.ok || !res.body) {
        let errMsg = `Request failed: ${res.status}`;
        try {
            const body = await res.json();
            if (body?.message) errMsg = body.message;
        } catch { /* noop */ }
        throw new Error(errMsg);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
            if (!part.startsWith("data: ")) continue;
            const json = part.slice(6).trim();
            if (!json) continue;
            try { yield JSON.parse(json); }
            catch (err) { console.error("[agents] failed to parse SSE chunk", err, json); }
        }
    }
}

// Streams an agent run as Server-Sent Events. Yields each parsed `data:` payload
// emitted by the lambda — one of:
//   { type: "thread",      threadId, agentId, isNewThread }
//   { type: "user_prompt", prompt, attachments }
//   { type: "ai_delta",    id, text }                  // live token
//   { type: "tool_call",   id, name, args }
//   { type: "tool_result", id, name, result }
//   { type: "interrupt",   id, payload }               // human-in-the-loop
//   { type: "error",       message }
//   { type: "end",         threadId }
export async function* streamChat({ agentId, message, threadId, attachments, signal }) {
    const res = await agentsFetch(`${requireAgentsUrl()}/chat`, {
        method: "POST",
        body: JSON.stringify({ agentId, message, threadId, attachments }),
        signal,
    });
    yield* parseSseStream(res);
}

// Turn-based Nova Sonic writer on the agents Function URL.
// Yields { type: "ai_delta"|"audio"|"speech_end"|"transcript"|"reply"|"draft"|"end"|"error" }.
export const warmBlogWriter = () => agentsFetch(`${requireAgentsUrl()}/blog-writer`).catch(() => {});

export async function* streamBlogWriter({ messages, title, body, text, audioBase64, signal }) {
    const res = await agentsFetch(`${requireAgentsUrl()}/blog-writer`, {
        method: "POST",
        body: JSON.stringify({ messages, title, body, text, audioBase64 }),
        signal,
    });
    yield* parseSseStream(res);
}

// Resumes a graph that paused on `interrupt()`. The decision is what the
// paused tool's interrupt() call will return.
//   decision = { approved: boolean, payload?: object, reason?: string }
export async function* resumeChat({ agentId, threadId, interruptId, decision, signal }) {
    const res = await agentsFetch(`${requireAgentsUrl()}/chat/resume`, {
        method: "POST",
        body: JSON.stringify({ agentId, threadId, interruptId, decision }),
        signal,
    });
    yield* parseSseStream(res);
}
